import React, { useState, useMemo, useRef } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sparkles, X, History, Clock, AlertTriangle, CheckCircle2, MoveRight, RotateCw } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * VehicleDamageMap — exploded car parts diagram where each body part is itself
 * the clickable hotspot. Uses the pre-cleaned `/car-parts-clean-v5.png` image
 * (900x1400) — no numbered dots, no legend. Hotspots are transparent rect
 * regions sized to cover each part. On hover they light up in CI blue; when a
 * status is set a translucent status-colored overlay marks the part.
 */

// (x, y) = center of the bounding box as % of the 1024x1536 clean image.
// (w, h) = bounding-box size in % of the image width / height.
// Bounding boxes derived automatically from the user's manually-drawn blue outlines.
export const PARTS = [
  { id: "motorhaube",       n: 1,  label: "Motorhaube",           x: 49.3, y: 24.3, w: 35.2, h: 10.9 },
  { id: "frontscheibe",     n: 2,  label: "Frontscheibe",         x: 49.3, y: 34.4, w: 33.3, h: 8.6 },
  { id: "dach",             n: 3,  label: "Dach",                 x: 49.1, y: 47.5, w: 34.0, h: 15.7 },
  { id: "schiebedach",      n: 4,  label: "Schiebedach",          x: 49.3, y: 61.8, w: 20.5, h: 13.0 },
  { id: "heckscheibe",      n: 5,  label: "Heckscheibe",          x: 49.3, y: 77.0, w: 26.4, h: 10.8 },
  { id: "kofferraumdeckel", n: 6,  label: "Kofferraumdeckel",     x: 49.0, y: 87.8, w: 25.8, h: 7.1 },
  { id: "frontstossstange", n: 7,  label: "Frontstoßstange",      x: 49.4, y: 8.6,  w: 44.6, h: 9.6 },
  { id: "heckstossstange",  n: 8,  label: "Heckstoßstange",       x: 49.1, y: 95.3, w: 37.5, h: 6.5 },
  { id: "spiegel_l",        n: 9,  label: "Linker Außenspiegel",  x: 25.6, y: 43.6, w: 7.7,  h: 3.8 },
  { id: "spiegel_r",        n: 10, label: "Rechter Außenspiegel", x: 73.6, y: 43.6, w: 7.6,  h: 3.8 },
  { id: "tuer_vl",          n: 11, label: "Linke Vordertür",      x: 20.5, y: 56.0, w: 14.9, h: 15.6 },
  { id: "tuer_vr",          n: 12, label: "Rechte Vordertür",     x: 78.0, y: 56.0, w: 14.8, h: 15.6 },
  { id: "tuer_hl",          n: 13, label: "Linke Hintertür",      x: 19.5, y: 73.0, w: 12.6, h: 14.3 },
  { id: "tuer_hr",          n: 14, label: "Rechte Hintertür",     x: 78.5, y: 73.0, w: 12.7, h: 14.3 },
  { id: "frontgrill",       n: 17, label: "Frontgrill",           x: 49.7, y: 16.3, w: 23.6, h: 3.1 },
  { id: "scheinwerfer_l",   n: 18, label: "Linker Scheinwerfer",  x: 25.4, y: 23.2, w: 5.0,  h: 7.9 },
  { id: "scheinwerfer_r",   n: 19, label: "Rechter Scheinwerfer", x: 73.9, y: 23.3, w: 4.7,  h: 7.9 },
  { id: "ruecklicht_l",     n: 20, label: "Linkes Rücklicht",     x: 30.0, y: 89.5, w: 7.0,  h: 3.1 },
  { id: "ruecklicht_r",     n: 21, label: "Rechtes Rücklicht",    x: 67.8, y: 89.5, w: 6.9,  h: 3.1 },
  { id: "rad_vl",           n: 22, label: "Rad vorne links",      x: 21.7, y: 34.1, w: 10.7, h: 10.2 },
  { id: "rad_vr",           n: 23, label: "Rad vorne rechts",     x: 77.2, y: 34.1, w: 10.5, h: 10.2 },
  { id: "rad_hl",           n: 24, label: "Rad hinten links",     x: 17.2, y: 90.4, w: 10.4, h: 9.8 },
  { id: "rad_hr",           n: 25, label: "Rad hinten rechts",    x: 80.6, y: 90.5, w: 10.5, h: 9.8 },
];

// Status options (colors chosen for clarity against the white diagram)
export const STATUS = {
  neu: { label: "Neuschaden", short: "Neu", bg: "#dc2626", text: "#ffffff", tint: "rgba(220,38,38,0.28)",   badge: "bg-rose-100 text-rose-800 border-rose-200",       icon: AlertTriangle },
  alt: { label: "Altschaden", short: "Alt", bg: "#ea580c", text: "#ffffff", tint: "rgba(234,88,12,0.28)",   badge: "bg-orange-100 text-orange-800 border-orange-200", icon: Clock },
  vor: { label: "Vorschaden", short: "Vor", bg: "#475569", text: "#ffffff", tint: "rgba(71,85,105,0.32)",   badge: "bg-slate-100 text-slate-700 border-slate-300",    icon: History },
};

// Vorschlags-Chips für Bauteile ohne eigenen Diagramm-Bereich — werden als
// "custom_"-Einträge im selben pre_damages-Dict gespeichert (mit label).
export const CUSTOM_PART_SUGGESTIONS = [
  "A-Säule links", "A-Säule rechts",
  "B-Säule links", "B-Säule rechts",
  "C-Säule links", "C-Säule rechts",
  "Schweller links", "Schweller rechts",
  "Seitenwand hinten links", "Seitenwand hinten rechts",
  "Unterboden", "Dachrahmen",
];

export default function VehicleDamageMap({ value = {}, arrows: arrowsProp, onChange, onArrowsChange, readOnly = false }) {
  const [openId, setOpenId] = useState(null);
  const [arrowMode, setArrowMode] = useState(false);
  const dragRef = useRef({ id: null, startX: 0, startY: 0, baseX: 0, baseY: 0 });
  const containerRef = useRef(null);

  // Arrows: prefer the dedicated `arrows` prop. Fall back to value._arrows
  // for legacy state. The new format is the canonical one.
  const arrows = Array.isArray(arrowsProp)
    ? arrowsProp
    : Array.isArray(value._arrows) ? value._arrows : [];

  const setArrows = (next) => {
    if (onArrowsChange) {
      onArrowsChange(next);
      return;
    }
    // legacy fallback: stash inside value (only used when parent hasn't been migrated)
    const v = { ...(value || {}) };
    if (next.length === 0) delete v._arrows;
    else v._arrows = next;
    onChange?.(v);
  };

  const addArrow = (e) => {
    if (!arrowMode || readOnly) return;
    if (e.target.closest("[data-arrow-handle]")) return; // ignore clicks on existing arrow controls
    if (e.target.closest("[data-part-hotspot]")) return; // ignore clicks on parts
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const id = `arr_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    setArrows([...arrows, { id, x, y, angle: 0 }]);
  };

  const updateArrow = (id, patch) => {
    setArrows(arrows.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  };
  const deleteArrow = (id) => setArrows(arrows.filter((a) => a.id !== id));

  const onArrowMouseDown = (e, a) => {
    e.stopPropagation();
    e.preventDefault();
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = {
      id: a.id,
      startX: e.clientX,
      startY: e.clientY,
      baseX: a.x,
      baseY: a.y,
      rectW: rect.width,
      rectH: rect.height,
    };
    const onMove = (ev) => {
      const r = dragRef.current;
      if (!r.id) return;
      const dx = ((ev.clientX - r.startX) / r.rectW) * 100;
      const dy = ((ev.clientY - r.startY) / r.rectH) * 100;
      const next = arrows.map((x) => (x.id === r.id ? { ...x, x: Math.max(0, Math.min(100, r.baseX + dx)), y: Math.max(0, Math.min(100, r.baseY + dy)) } : x));
      setArrows(next);
    };
    const onUp = () => {
      dragRef.current.id = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const counts = useMemo(() => {
    const out = { neu: 0, alt: 0, vor: 0, total: 0 };
    Object.entries(value || {}).forEach(([key, v]) => {
      if (key === "_arrows") return; // skip arrow array
      const statuses = getStatusList(v);
      if (statuses.length === 0) return;
      out.total += 1;
      statuses.forEach((s) => { if (out[s] !== undefined) out[s] += 1; });
    });
    return out;
  }, [value]);

  function getStatusList(v) {
    if (!v) return [];
    if (Array.isArray(v.statuses) && v.statuses.length) return v.statuses.filter((s) => STATUS[s]);
    if (v.status && STATUS[v.status]) return [v.status];
    return [];
  }

  const getStatuses = (id) => getStatusList(value?.[id]);
  const getNote = (id) => value?.[id]?.note || "";

  const toggleStatus = (id, status) => {
    const current = getStatuses(id);
    const exists = current.includes(status);
    const next = { ...(value || {}) };
    const newStatuses = exists ? current.filter((s) => s !== status) : [...current, status];
    const note = next[id]?.note || "";
    // Custom-Einträge (mit label) bleiben auch ohne Status/Notiz erhalten —
    // sie werden explizit über das X in der Liste entfernt.
    if (newStatuses.length === 0 && !note && !next[id]?.label) delete next[id];
    else next[id] = { ...(next[id] || {}), statuses: newStatuses, note };
    onChange?.(next);
  };

  const setNote = (id, note) => {
    const next = { ...(value || {}) };
    const cur = getStatuses(id);
    next[id] = { ...(next[id] || {}), statuses: cur, note };
    if (cur.length === 0 && !note && !next[id]?.label) delete next[id];
    onChange?.(next);
  };

  // ---------- Custom-Bauteile (z.B. A-/B-Säule) ohne Diagramm-Bereich ------
  const customEntries = useMemo(() =>
    Object.entries(value || {})
      .filter(([k, v]) => k.startsWith("custom_") && v && v.label)
      .map(([id, v]) => ({ id, label: v.label })),
  [value]);

  const addCustomPart = (label) => {
    const t = (label || "").trim();
    if (!t) return;
    const slug = t.toLowerCase().replace(/[^a-z0-9äöüß]+/g, "_").replace(/^_+|_+$/g, "");
    const id = `custom_${slug || Date.now()}`;
    if (value?.[id]) return;
    onChange?.({ ...(value || {}), [id]: { label: t, statuses: ["vor"], note: "" } });
  };

  const removeCustomPart = (id) => {
    const next = { ...(value || {}) };
    delete next[id];
    onChange?.(next);
  };

  const resetAll = () => onChange?.({});

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white overflow-hidden" data-testid="vehicle-damage-map">
      {/* Header */}
      <div className="px-5 py-4 border-b border-zinc-200 bg-white flex items-center gap-3 flex-wrap">
        <div className="h-9 w-9 rounded-xl brand-gradient-bg flex items-center justify-center shrink-0">
          <Sparkles className="h-4 w-4 text-white" strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold tracking-tight text-zinc-900">
            Vorschaden-Erfassung
          </div>
          <div className="text-xs text-zinc-500">
            Bauteil antippen → Status wählen. Neu-/Alt-/Vorschaden je Bereich.
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {!readOnly && (
            <Button
              type="button"
              size="sm"
              variant={arrowMode ? "default" : "outline"}
              onClick={() => setArrowMode((m) => !m)}
              className={cn(
                "h-7 px-2.5 text-[11px] font-semibold",
                arrowMode && "brand-gradient-bg text-white border-transparent"
              )}
              data-testid="dmg-arrow-toggle"
            >
              <MoveRight className="h-3.5 w-3.5 mr-1" />
              {arrowMode ? "Pfeil-Modus aktiv" : "Pfeil setzen"}
            </Button>
          )}
          {Object.entries(STATUS).map(([k, v]) => (
            <span
              key={k}
              className={cn("text-[11px] font-bold border rounded-full px-2.5 py-1 inline-flex items-center gap-1", v.badge)}
              data-testid={`dmg-legend-${k}`}
            >
              <span className="h-2 w-2 rounded-full" style={{ background: v.bg }} />
              {v.short} · {counts[k]}
            </span>
          ))}
        </div>
      </div>

      {/* Body: 2-Spalten-Layout — Diagramm links, Schaden-Liste rechts.
          Auf mobile (< lg) stacked, auf Desktop nebeneinander. */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-0 lg:divide-x divide-zinc-100">
        {/* Image with invisible rect hotspots covering each part */}
        <div className="relative bg-white mx-auto w-full p-4" style={{ maxWidth: 580 }}>
          <div
            ref={containerRef}
            className={cn(
              "relative",
              arrowMode && !readOnly && "cursor-crosshair"
            )}
            style={{ aspectRatio: "1024 / 1536" }}
          onClick={addArrow}
        >
          <img
            src="/car-parts-clean-v5.png"
            alt="Fahrzeug-Bauteile"
            className="absolute inset-0 w-full h-full object-contain select-none pointer-events-none"
            draggable={false}
          />

          {/* Arrow overlay layer — sits below part hotspots so part-popovers still work */}
          {arrows.map((a) => (
            <div
              key={a.id}
              data-arrow-handle
              data-testid={`dmg-arrow-${a.id}`}
              className="absolute group"
              style={{
                left: `${a.x}%`,
                top: `${a.y}%`,
                width: 56,
                height: 56,
                marginLeft: -28,
                marginTop: -28,
                transform: `rotate(${a.angle || 0}deg)`,
                pointerEvents: readOnly ? "none" : "auto",
                zIndex: 30,
              }}
            >
              <svg
                viewBox="0 0 56 56"
                className={cn(
                  "absolute inset-0 w-full h-full drop-shadow-[0_2px_4px_rgba(0,0,0,0.35)]",
                  !readOnly && "cursor-grab active:cursor-grabbing"
                )}
                onMouseDown={(e) => !readOnly && onArrowMouseDown(e, a)}
              >
                {/* Arrow shaft */}
                <line x1="28" y1="6" x2="28" y2="46" stroke="#dc2626" strokeWidth="5" strokeLinecap="round" />
                {/* Arrow head */}
                <polygon points="28,52 18,38 38,38" fill="#dc2626" />
              </svg>
              {!readOnly && (
                <>
                  <button
                    type="button"
                    data-arrow-handle
                    onClick={(e) => { e.stopPropagation(); deleteArrow(a.id); }}
                    className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-zinc-900 text-white text-[10px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Pfeil löschen"
                    data-testid={`dmg-arrow-del-${a.id}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    data-arrow-handle
                    onClick={(e) => { e.stopPropagation(); updateArrow(a.id, { angle: ((a.angle || 0) + 45) % 360 }); }}
                    className="absolute -bottom-2 -right-2 h-5 w-5 rounded-full bg-cyan-600 text-white text-[10px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    title="45° drehen"
                    data-testid={`dmg-arrow-rot-${a.id}`}
                  >
                    <RotateCw className="h-3 w-3" />
                  </button>
                </>
              )}
            </div>
          ))}

          {arrowMode && !readOnly && arrows.length === 0 && (
            <div className="absolute inset-x-0 bottom-2 mx-auto w-fit pointer-events-none px-3 py-1.5 rounded-full bg-zinc-900/85 text-white text-[11px] font-medium shadow-lg">
              Klicke auf das Fahrzeug, um einen Pfeil zu setzen
            </div>
          )}

          {PARTS.map((p) => {
            const statuses = getStatuses(p.id);
            const open = openId === p.id;
            // Primary status (priority: Neu > Alt > Vor) drives the part tint
            const primary = ["neu", "alt", "vor"].find((s) => statuses.includes(s)) || null;
            const primaryMeta = primary ? STATUS[primary] : null;
            const tintBg = statuses.length > 1
              ? `repeating-linear-gradient(135deg, ${STATUS[statuses[0]].tint} 0 12px, ${STATUS[statuses[1] || statuses[0]].tint} 12px 24px${statuses[2] ? `, ${STATUS[statuses[2]].tint} 24px 36px` : ""})`
              : primaryMeta?.tint;
            return (
              <Popover key={p.id} open={open} onOpenChange={(o) => setOpenId(o ? p.id : null)}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    disabled={readOnly}
                    data-testid={`dmg-part-${p.id}`}
                    data-part-hotspot
                    aria-label={p.label}
                    title={p.label}
                    style={{
                      left: `${p.x - p.w / 2}%`,
                      top: `${p.y - p.h / 2}%`,
                      width: `${p.w}%`,
                      height: `${p.h}%`,
                      background: statuses.length > 0 ? tintBg : "transparent",
                      borderColor: open ? "#0ea5e9" : primaryMeta ? primaryMeta.bg : "transparent",
                    }}
                    className={cn(
                      "absolute rounded-md border-2 transition-all group",
                      "cursor-pointer hover:bg-cyan-500/15 hover:border-cyan-500",
                      "focus:outline-none focus-visible:border-cyan-500 focus-visible:bg-cyan-500/20"
                    )}
                  >
                    {/* Stack up to 3 status badges in the top-right corner */}
                    {statuses.length > 0 && (
                      <span className="absolute -top-2 -right-2 flex gap-0.5 pointer-events-none">
                        {statuses.map((s) => {
                          const m = STATUS[s];
                          return (
                            <span
                              key={s}
                              className="min-w-[20px] h-[20px] rounded-full px-1 text-[9px] font-bold flex items-center justify-center shadow-md ring-2 ring-white"
                              style={{ backgroundColor: m.bg, color: m.text }}
                            >
                              {m.short}
                            </span>
                          );
                        })}
                      </span>
                    )}
                    {/* Hover label chip */}
                    <span className="absolute left-1/2 -translate-x-1/2 -bottom-6 whitespace-nowrap text-[10px] font-semibold text-white bg-cyan-700/95 rounded px-1.5 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow">
                      {p.label}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent side="top" align="center" className="w-[260px] p-3">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-bold text-sm tracking-tight text-zinc-900 leading-tight truncate">
                        {p.label}
                      </div>
                      {statuses.length > 0 && (
                        <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">
                          {statuses.map((s) => STATUS[s].label).join(" · ")}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => setOpenId(null)}
                      className="text-zinc-400 hover:text-zinc-700 -mr-1"
                      aria-label="Schließen"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5 mb-2">
                    {Object.entries(STATUS).map(([k, v]) => {
                      const active = statuses.includes(k);
                      const Icon = v.icon;
                      return (
                        <button
                          key={k}
                          type="button"
                          disabled={readOnly}
                          onClick={() => toggleStatus(p.id, k)}
                          data-testid={`dmg-status-${p.id}-${k}`}
                          className={cn(
                            "h-12 rounded-md text-[11px] font-semibold flex flex-col items-center justify-center gap-0.5 border transition",
                            active ? "text-white shadow-inner" : "bg-white text-zinc-600 hover:bg-zinc-50 border-zinc-200"
                          )}
                          style={active ? { backgroundColor: v.bg, borderColor: v.bg } : undefined}
                        >
                          <Icon className="h-3.5 w-3.5" strokeWidth={2} />
                          {v.short}
                        </button>
                      );
                    })}
                  </div>
                  <Input
                    placeholder="Notiz (optional)"
                    value={getNote(p.id)}
                    onChange={(e) => setNote(p.id, e.target.value)}
                    disabled={readOnly}
                    className="h-8 text-xs"
                    data-testid={`dmg-note-${p.id}`}
                  />
                </PopoverContent>
              </Popover>
            );
          })}
        </div>
      </div>

        {/* Rechte Spalte: Strukturierte Schaden-Liste (Auflistung der
            markierten Bauteile gruppiert nach Status). Auf Mobile unter
            dem Diagramm, auf Desktop daneben. */}
        <DamagePartsList
          parts={PARTS}
          customEntries={customEntries}
          value={value}
          arrows={arrows}
          getStatuses={getStatuses}
          getNote={getNote}
          setNote={setNote}
          deleteArrow={deleteArrow}
          readOnly={readOnly}
        />
      </div>

      {/* Weitere Bauteile — manuelle Liste unter der Grafik (A-/B-Säule etc.) */}
      <CustomPartsSection
        entries={customEntries}
        suggestions={CUSTOM_PART_SUGGESTIONS.filter((s) => !customEntries.some((e) => e.label === s))}
        onAdd={addCustomPart}
        onRemove={removeCustomPart}
        toggleStatus={toggleStatus}
        setNote={setNote}
        getStatuses={getStatuses}
        getNote={getNote}
        readOnly={readOnly}
      />

      {/* Summary + bulk reset */}
      {counts.total > 0 && (
        <div className="border-t border-zinc-200 bg-zinc-50/40 px-4 py-3 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-zinc-500">
            Erfasste Bauteile · {counts.total}
          </div>
          {!readOnly && (
            <button
              type="button"
              onClick={resetAll}
              className="text-[11px] font-medium text-rose-600 hover:underline"
              data-testid="dmg-reset-all"
            >
              Alle zurücksetzen
            </button>
          )}
        </div>
      )}
    </div>
  );
}


// ---------- DamagePartsList ----------------------------------------------
// Strukturierte Liste der erfassten Schäden, gruppiert nach Status
// (Neuschaden / Altschaden / Vorschaden / Pfeil-Marker). Wird neben dem
// Schadendiagramm gerendert und erlaubt In-Place-Editing der Notiz.
function DamagePartsList({ parts, customEntries, value, arrows, getStatuses, getNote, setNote, deleteArrow, readOnly }) {
  // Gruppiere die markierten Bauteile nach Status. Multi-Status-Teile
  // erscheinen in jeder zutreffenden Gruppe.
  const buckets = { neu: [], alt: [], vor: [] };
  const allParts = [...parts, ...(customEntries || []).map((e) => ({ id: e.id, label: e.label, n: "•" }))];
  for (const p of allParts) {
    const sts = getStatuses(p.id);
    if (sts.length === 0) continue;
    for (const s of sts) {
      if (buckets[s]) {
        buckets[s].push({ ...p, note: getNote(p.id) });
      }
    }
  }
  const STATUS_ORDER = ["neu", "alt", "vor"];
  const hasAny = STATUS_ORDER.some((k) => buckets[k].length > 0) || (arrows && arrows.length > 0);

  return (
    <div className="bg-zinc-50/30 p-4 lg:p-5 max-h-[700px] overflow-y-auto" data-testid="dmg-side-list">
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-zinc-500 mb-3">
        Erfasste Schäden
      </div>

      {!hasAny ? (
        <div className="text-xs text-zinc-400 italic py-6 text-center">
          Noch keine Bauteile markiert.<br />
          Tippe auf einen Bereich im Diagramm.
        </div>
      ) : (
        <div className="space-y-4">
          {STATUS_ORDER.map((statusKey) => {
            const bucket = buckets[statusKey];
            if (bucket.length === 0) return null;
            const meta = STATUS[statusKey];
            return (
              <div key={statusKey} data-testid={`dmg-side-bucket-${statusKey}`}>
                <div
                  className={cn(
                    "flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider mb-2 pb-1 border-b",
                    meta.badge
                  )}
                  style={{ borderColor: meta.bg + "33" }}
                >
                  <span
                    className="h-2.5 w-2.5 rounded-full shrink-0"
                    style={{ background: meta.bg }}
                  />
                  <span>{meta.label}</span>
                  <span className="ml-auto text-[10px] font-normal opacity-60">
                    {bucket.length}
                  </span>
                </div>
                <ul className="space-y-1.5 ml-1">
                  {bucket.map((p) => (
                    <li key={`${statusKey}-${p.id}`} className="text-xs" data-testid={`dmg-side-item-${p.id}-${statusKey}`}>
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-zinc-400 font-mono text-[10px] shrink-0 w-4">{p.n}.</span>
                        <span className="font-medium text-zinc-900 leading-tight">{p.label}</span>
                      </div>
                      {p.note && (
                        <div className="text-[11px] text-zinc-500 italic ml-5 mt-0.5 leading-tight">
                          {p.note}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}

          {/* Pfeil-Marker */}
          {arrows && arrows.length > 0 && (
            <div data-testid="dmg-side-arrows">
              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-cyan-700 mb-2 pb-1 border-b border-cyan-200">
                <MoveRight className="h-3 w-3" />
                <span>Marker</span>
                <span className="ml-auto text-[10px] font-normal opacity-60">{arrows.length}</span>
              </div>
              <ul className="space-y-1.5 ml-1">
                {arrows.map((a, i) => (
                  <li key={a.id} className="text-xs flex items-center gap-2 group">
                    <span className="text-zinc-400 font-mono text-[10px] w-4">{i + 1}.</span>
                    <span className="font-medium text-zinc-900">Marker {i + 1}</span>
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => deleteArrow(a.id)}
                        className="ml-auto opacity-0 group-hover:opacity-100 text-rose-500 hover:text-rose-700"
                        data-testid={`dmg-side-arrow-delete-${a.id}`}
                        aria-label="Marker löschen"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ---------- CustomPartsSection --------------------------------------------
// Manuelle Bauteil-Liste unter der Grafik: A-/B-/C-Säule, Schweller etc.
// haben keinen eigenen Diagramm-Bereich — hier können sie als Einträge mit
// Status (Neu/Alt/Vor) und Notiz erfasst werden. Landen im selben
// pre_damages-Dict (Key: custom_<slug>, mit `label`) und damit im PDF.
function CustomPartsSection({ entries, suggestions, onAdd, onRemove, toggleStatus, setNote, getStatuses, getNote, readOnly }) {
  const [text, setText] = useState("");
  const add = () => {
    const t = text.trim();
    if (!t) return;
    onAdd(t);
    setText("");
  };
  if (readOnly && entries.length === 0) return null;
  return (
    <div className="border-t border-zinc-200 px-4 lg:px-5 py-4" data-testid="dmg-custom-section">
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-zinc-500 mb-1">
        Weitere Bauteile
      </div>
      <div className="text-xs text-zinc-500 mb-3">
        Bauteile ohne Diagramm-Bereich (z.&nbsp;B. A-/B-Säule, Schweller) hier auflisten — erscheinen ebenfalls im Gutachten.
      </div>

      {!readOnly && (
        <>
          {suggestions.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {suggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => onAdd(s)}
                  className="text-[11px] border border-zinc-300 rounded-full px-2.5 py-1 text-zinc-600 hover:border-cyan-400 hover:text-cyan-700 hover:bg-cyan-50 transition-colors"
                  data-testid={`dmg-custom-suggest-${s.toLowerCase().replace(/[^a-zäöüß0-9]+/g, "-")}`}
                >
                  + {s}
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-2 mb-3 max-w-md">
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
              placeholder="Eigenes Bauteil eintragen (z.B. A-Säule links)"
              className="h-8 text-xs"
              data-testid="dmg-custom-input"
            />
            <Button type="button" size="sm" onClick={add} disabled={!text.trim()} className="h-8 text-xs shrink-0" data-testid="dmg-custom-add-btn">
              Hinzufügen
            </Button>
          </div>
        </>
      )}

      {entries.length > 0 && (
        <ul className="space-y-2" data-testid="dmg-custom-list">
          {entries.map((e) => {
            const sts = getStatuses(e.id);
            return (
              <li
                key={e.id}
                className="flex flex-wrap items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50/60 px-3 py-2"
                data-testid={`dmg-custom-item-${e.id}`}
              >
                <span className="text-xs font-semibold text-zinc-900 min-w-[120px]">{e.label}</span>
                <span className="flex gap-1">
                  {Object.entries(STATUS).map(([k, v]) => {
                    const active = sts.includes(k);
                    return (
                      <button
                        key={k}
                        type="button"
                        disabled={readOnly}
                        onClick={() => toggleStatus(e.id, k)}
                        className={cn(
                          "text-[10px] font-bold rounded-full px-2 py-0.5 border transition",
                          active ? "text-white" : "bg-white text-zinc-500 border-zinc-200 hover:bg-zinc-100"
                        )}
                        style={active ? { backgroundColor: v.bg, borderColor: v.bg } : undefined}
                        data-testid={`dmg-custom-status-${e.id}-${k}`}
                      >
                        {v.short}
                      </button>
                    );
                  })}
                </span>
                <Input
                  value={getNote(e.id)}
                  onChange={(ev) => setNote(e.id, ev.target.value)}
                  placeholder="Notiz (optional)"
                  disabled={readOnly}
                  className="h-7 text-xs flex-1 min-w-[140px] bg-white"
                  data-testid={`dmg-custom-note-${e.id}`}
                />
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => onRemove(e.id)}
                    className="text-rose-500 hover:text-rose-700 shrink-0"
                    data-testid={`dmg-custom-del-${e.id}`}
                    aria-label="Entfernen"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

